import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCerrarCorreccion, useReabrirCorreccion } from "@/hooks/useGeneracion";
import { useReleerLaRedaccion } from "@/components/generacion/redaccionAutomatica";
import { getApiErrorMessage } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import {
  etiquetaDelDestino,
  textoDeLaCorreccion,
  type CorreccionDeDuda,
  type DestinoDeLaCorreccion,
  type Duda,
} from "@/lib/generacion";
import { esPestanaDelInforme, rutaDePestana } from "@/lib/informes";

interface BotonIrACorregirProps {
  informeId: number;
  destino: DestinoDeLaCorreccion;
  /** Lleva a la fase de valorización; sin él el botón de ese destino no se dibuja. */
  onIrALaValorizacion?: () => void;
}

/**
 * Abre el lugar donde se corrige el dato: la pestaña del informe (con la fila en el `#`
 * cuando el servidor la identificó) o la fase de valorización.
 */
export function BotonIrACorregir({ informeId, destino, onIrALaValorizacion }: BotonIrACorregirProps) {
  const navigate = useNavigate();

  if (destino.tipo === "valorizacion") {
    return onIrALaValorizacion ? (
      <BotonDeDestino etiqueta={etiquetaDelDestino(destino)} onClick={onIrALaValorizacion} />
    ) : null;
  }

  const pestana = destino.pestana;

  if (pestana === null || !esPestanaDelInforme(pestana)) return null;

  const ancla = destino.fila ? `#fila-${destino.fila.tipo}-${destino.fila.id}` : "";

  return (
    <BotonDeDestino
      etiqueta={etiquetaDelDestino(destino)}
      onClick={() => void navigate(`${rutaDePestana(informeId, pestana)}${ancla}`)}
    />
  );
}

function BotonDeDestino({ etiqueta, onClick }: { etiqueta: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-auto rounded-[0.625rem] px-3 py-1.5"
      onClick={onClick}
    >
      {etiqueta}
    </Button>
  );
}

interface CorreccionDeLaDudaProps {
  informeId: number;
  duda: Duda;
  correccion: CorreccionDeDuda;
  onIrALaValorizacion?: () => void;
}

/**
 * La corrección de una duda respondida: lo que la respuesta dijo que DEBE ser el dato, lo
 * que la hoja muestra hoy y el camino para corregirlo. Mientras esté pendiente cierra la
 * redacción y la emisión; se cumple sola cuando la hoja muestra el valor, o una persona
 * deja constancia, con motivo, de que lo corrigió de otra forma.
 */
export function CorreccionDeLaDuda({ informeId, duda, correccion, onIrALaValorizacion }: CorreccionDeLaDudaProps) {
  const [cerrando, setCerrando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const cerrar = useCerrarCorreccion(informeId);
  const reabrir = useReabrirCorreccion(informeId);
  // Cerrar la última corrección arranca la redacción en el servidor: hay que releerla.
  const releerLaRedaccion = useReleerLaRedaccion(informeId);

  if (correccion.estado === "cumplida") {
    return (
      <div className="bg-success-soft text-success-soft-foreground rounded-[0.875rem] px-4 py-2.5 text-xs font-bold">
        Corrección cumplida: {correccion.campo} ya muestra {correccion.valor_actual ?? correccion.valor_esperado}.
      </div>
    );
  }

  if (correccion.estado === "cerrada_manual") {
    const cuando = correccion.cerrada?.en ? formatearFechaYHora(correccion.cerrada.en) : null;
    const quien = correccion.cerrada?.por?.nombre ?? null;

    return (
      <div className="bg-superficie-tenue border-border flex flex-wrap items-start gap-3 rounded-[0.875rem] border-[1.5px] px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-ink text-xs font-bold">
            {correccion.campo}: corregido de otra forma
          </span>
          <span className="text-ink-muted text-xs font-semibold">
            {[quien ? `Por ${quien}` : null, cuando].filter(Boolean).join(" · ")}
          </span>
          <span className="text-ink-body text-xs text-pretty">{correccion.cerrada?.motivo}</span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-auto rounded-[0.625rem] px-3 py-1.5"
          disabled={reabrir.isPending}
          onClick={() =>
            reabrir.mutate(duda.id, {
              onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos reabrir la corrección.")),
            })
          }
        >
          Reabrir
        </Button>
      </div>
    );
  }

  function confirmar() {
    cerrar.mutate(
      { dudaId: duda.id, motivo: motivo.trim() },
      {
        onSuccess: () => {
          releerLaRedaccion();
          setCerrando(false);
          setMotivo("");
          toast.success("Corrección cerrada");
        },
        onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos cerrar la corrección.")),
      },
    );
  }

  return (
    <div className="bg-warning-soft flex flex-col gap-2.5 rounded-[0.875rem] px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-warning-soft-foreground min-w-0 flex-1 text-xs font-bold text-pretty">
          {textoDeLaCorreccion(correccion)}
        </span>
        <BotonIrACorregir
          informeId={informeId}
          destino={correccion.destino}
          onIrALaValorizacion={onIrALaValorizacion}
        />
        {!cerrando ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-auto rounded-[0.625rem] px-3 py-1.5"
            onClick={() => setCerrando(true)}
          >
            Lo corregí de otra forma
          </Button>
        ) : null}
      </div>
      <p className="text-ink-muted text-xs font-semibold text-pretty">
        Responder no cambió el dato: corrígelo en su pestaña y la corrección se cumple sola. Mientras tanto la IA no
        redacta ni se puede emitir.
      </p>

      {cerrando ? (
        <div className="flex flex-col gap-2">
          <Textarea
            aria-label="Cómo lo corregiste"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Explica cómo lo corregiste (por ejemplo, excluí la fila en la valorización)"
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-auto rounded-[0.625rem] px-3 py-1.5"
              onClick={() => setCerrando(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="fuerte"
              className="h-auto rounded-[0.625rem] px-3 py-1.5"
              disabled={motivo.trim() === "" || cerrar.isPending}
              onClick={confirmar}
            >
              Cerrar la corrección
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
